import { OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Constructor } from './common.models';

/**
 * Mixins dynamically extend functionality of a base class
 *
 * @example
 *
 * export function exampleMixin<T extends Constructor<{}>>(Base: T) {
 *   return class MixinClass extends Base implements OnDestroy {
 *      // add some functionality
 *       constructor(...args: any[]) {
 *           super(...args);
 *       }
 *      // add some functionality
 *   };
 *  }
 *
 * example.component.ts:
 * export class ExampleComponent extends exampleMixin(anotherMixin(yetAnotherMixin(BaseClass)))
 *
 * BaseClass can be an empty class
 * args contains the constructor params of the base class
 *
 * IMPORTANT:
 * To prevent overwriting life cycle hooks of other mixins and base class,
 * the mixin should extend it's type param with the the corresponding hooks and the inherited hooks must be called with super:
 *
 * exampleMixin<T extends Constructor<OnDestroy>>
 * ngOnDestroy() {
 *      if (typeof super.ngOnDestroy === 'function') {
 *          super.ngOnDestroy();
 *       }
 *       // mixin functionality
 *  }

 */
export function withSubscription<T extends Constructor<OnDestroy>>(Base: T) {
    return class MixinClass extends Base implements OnDestroy {
        sub = new Subscription();
        constructor(...args: any[]) {
            super(...args);
        }

        ngOnDestroy() {
            if (typeof super.ngOnDestroy === 'function') {
                super.ngOnDestroy();
            }
            this.sub.unsubscribe();
        }
    };
}
